import React from 'react';
import { PoundSterling, Info } from 'lucide-react';
import { ProviderBadge } from '../ProviderBadge';
import { PayoutEquation, BetaFeeTiles } from './PayoutEquation';

export function FeeBreakdown({ earnings }) {
  const isBeta = earnings.beta_fee_percent != null;
  return (
    <div className="card-surface p-6" data-testid="fee-breakdown">
      <h3 className="text-sm font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
        <PoundSterling className="h-4 w-4 text-[#D4AF37]" />
        Your Fee Structure
        <div className="ml-auto flex items-center gap-2">
          {earnings.badge && <ProviderBadge badge={earnings.badge} size="xs" />}
          <span className="px-2 py-0.5 rounded-full bg-[#F9F6F0] text-xs text-[#D4AF37] font-medium capitalize">
            {isBeta ? 'Founding (Beta)' : `${earnings.tier} tier`}
          </span>
        </div>
      </h3>

      {/* Badge discount banner */}
      {!isBeta && earnings.badge_active && earnings.badge && (
        <div className={`mb-4 p-3 rounded-lg border ${
          earnings.badge === 'founding'
            ? 'bg-[#D4AF37]/5 border-[#D4AF37]/20'
            : 'bg-purple-50 border-purple-200'
        }`} data-testid="badge-discount-banner">
          <div className="flex items-center gap-2">
            <ProviderBadge badge={earnings.badge} size="sm" />
            <div className="text-xs">
              <p className="font-semibold text-[#1A1A1A]">
                {earnings.effective_platform_rate === 0
                  ? 'You currently pay 0% platform commission!'
                  : `You currently pay ${(earnings.effective_platform_rate * 100).toFixed(0)}% commission (instead of ${(earnings.tier_config.platform_rate * 100).toFixed(0)}%)`
                }
              </p>
              {earnings.badge_expires_at && (
                <p className="text-stone-500 mt-0.5">
                  Benefit active until {new Date(earnings.badge_expires_at).toLocaleDateString('en-GB', {day: 'numeric', month: 'long', year: 'numeric'})}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {isBeta ? <BetaFeeTiles earnings={earnings} /> : (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 bg-stone-50 rounded-lg text-center">
          <p className="text-2xl font-semibold text-[#1A1A1A]" data-testid="effective-commission-rate">
            {(earnings.effective_platform_rate * 100).toFixed(0)}%
          </p>
          <p className="text-xs text-stone-500 mt-1">
            Platform Commission
            {earnings.badge_active && (
              <span className="block text-emerald-600 font-medium">
                (normally {(earnings.tier_config.platform_rate * 100).toFixed(0)}%)
              </span>
            )}
          </p>
        </div>
        <div className="p-4 bg-stone-50 rounded-lg text-center">
          <p className="text-2xl font-semibold text-[#1A1A1A]">{(earnings.tier_config.processing_rate * 100).toFixed(2)}%</p>
          <p className="text-xs text-stone-500 mt-1">Processing Fee Rate</p>
        </div>
        <div className="p-4 bg-stone-50 rounded-lg text-center">
          <p className="text-2xl font-semibold text-[#1A1A1A]">20p</p>
          <p className="text-xs text-stone-500 mt-1">Fixed Processing Fee</p>
        </div>
      </div>
      )}

      <PayoutEquation earnings={earnings} />

      {!isBeta && (
      <div className="mt-4 p-3 bg-[#F9F6F0] rounded-lg flex items-start gap-2">
        <Info className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
        <p className="text-xs text-stone-600">
          <strong>Example:</strong> On a £100 booking you currently receive{' '}
          <strong>£{(100 - (100 * earnings.effective_platform_rate) - (100 * earnings.tier_config.processing_rate + earnings.tier_config.processing_fixed)).toFixed(2)}</strong>{' '}
          after fees.
        </p>
      </div>
      )}
    </div>
  );
}
